import { mobile, tablet } from "components/templates/Breakpoints";
import styled, { css } from "styled-components";
import { color } from "styles";
import { P1, P2 } from "components/atoms/Paragraph";
import TextBlock from "../common/TextBlock";
import { Repeater, Text, types } from "react-bricks";
import { getVerticalPadding, VerticalPadding, verticalPadSideProp } from "../utils/verticalPadding";
import { H2 } from "components/atoms/Heading";
import { backgroundColorProp } from "../utils/backgroundColor";
import TextHeading from "../common/TextHeading";

interface HeroTextProps {
  backgroundColor?: types.IColor;
  titleColor: string;
  paddingVertical?: VerticalPadding;
  eyebrowColor?: string;
  eyebrow?: string;
  showBody?: boolean;
}

export const HeroText: types.Brick<HeroTextProps> = ({
  eyebrow,
  eyebrowColor = color.H100,
  titleColor = color.H100,
  backgroundColor,
  paddingVertical,
  showBody = true,
}) => {
  const pad = getVerticalPadding(paddingVertical);
  return (
    <Wrapper pb={pad} pt={pad} backgroundColor={backgroundColor?.color}>
      <TextWrap>
        {eyebrow && (
          <Eyebrow color={eyebrowColor} align="center">
            {eyebrow}
          </Eyebrow>
        )}

        <TextHeading
          propName="title"
          placeholder="Enter Title"
          color={titleColor}
          padBottom={showBody}
        />

        {showBody && <TextBlock propName="description" placeholder="Description" align="center" />}

        <Repeater
          propName="buttons"
          renderWrapper={(items) => <ButtonWrapper>{items}</ButtonWrapper>}
        />
      </TextWrap>
    </Wrapper>
  );
};

HeroText.schema = {
  name: "hero-text",
  label: "Hero Text",
  previewImageUrl: `/static/bricks/hero-text.png`,
  getDefaultProps: () => ({
    title: "This is a custom Hero Unit",
    paddingVertical: "m",
    showBody: true,
    showFootnote: false,
    description:
      "We are a hi-tech web development company committed to deliver great products on time. We love to understand our customers' needs and exceed expectations.",
  }),
  repeaterItems: [
    {
      name: "buttons",
      itemType: "buttonstrip",
      itemLabel: "Button Strip",
      min: 0,
      max: 1,
    },
  ],
  sideEditProps: [
    verticalPadSideProp,
    backgroundColorProp,
    {
      name: "showBody",
      label: "Show body copy?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
  ],
};

export default HeroText;

const Wrapper = styled.div<{ backgroundColor?: string; pb?: string; mobPb?: string; pt?: string }>`
  padding: 6.875rem;
  padding-top: ${(p) => p.pt};
  padding-bottom: ${(p) => p.pb};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${(p) => p.backgroundColor || `${color.B210}`};

  ${tablet} {
    padding-bottom: ${(p) => p.mobPb};
  }

  ${mobile} {
    padding: 4rem 2rem 4rem;
    padding-bottom: ${(p) => p.mobPb};
  }
`;

const TextWrap = styled.div<{ maxWidth?: string }>`
  max-width: ${(p) => p.maxWidth || "58.125rem"};
`;

const ButtonWrapper = styled.div`
  margin-top: 4rem;
`;

const Footnote = styled(P2)`
  max-width: 46rem;
`;

const Eyebrow = styled(P1)`
  font-weight: bold;
  padding-bottom: 1rem;
`;

const Title = styled(H2)<{
  $titleFontSize?: [string, string, string];
  padBottom: boolean;
}>`
  margin-bottom: ${(p) => (p.padBottom ? `1.5rem` : `0`)};
  ${(p) =>
    p.$titleFontSize &&
    css`
      font-size: ${p.$titleFontSize[0]};

      ${tablet} {
        font-size: ${p.$titleFontSize[1]};
      }

      ${mobile} {
        font-size: ${p.$titleFontSize[2]};
      }
    `}
`;
